import React, { useState, useCallback } from 'react';
import {
  Box,
  List,
  Grid,
  Link,
  Button,
  Divider,
  ListItem,
  Typography,
  ListItemText,
} from '@material-ui/core';
import clsx from 'clsx';
import i18n from 'i18n-js';
import { useConfirm } from 'hooks';
import PropTypes from 'prop-types';
import { formatAmount } from 'utils';
import Loading from 'components/Loading';
import AddIcon from '@material-ui/icons/Add';
import { InvoiceItemModal } from 'components/Modals';
import { Card, CollapseCard } from 'components/UI';
import { useSelector, useDispatch } from 'react-redux';
import { InvoiceActions } from 'state/modules/invoice';
import { useStyles, collapseCardStyles, listTextStyles } from './InvoiceItems.styled';

const ListTitleComponent = ({ title, total }) => {
  const classes = useStyles();
  return (
    <Grid container className={classes.titleRow}>
      <span style={{ flexGrow: 1, textAlign: 'left', marginRight: '5px' }}>{title}</span>
      <span style={{ flexGrow: 0 }} data-cy="invoice-item-title-description">
        {i18n.t('invoice.total_info', {
          total: formatAmount(total),
        })}
      </span>
    </Grid>
  );
};

const ItemComponent = ({ item, handleEdit, handleDelete }) => {
  const collapseCardClasses = collapseCardStyles();
  const listClasses = listTextStyles();
  const classes = useStyles();
  const isEditable = !item?.invoice_item_type?.pricing_type?.internal_items;

  const {
    description,
    unit_price,
    quantity,
    q_humanized,
    line_pre_tax: total,
    invoice_item_type: {
      name,
      description: itemDesc,
      units: { total: unit },
    },
  } = item;

  const invoiceItemInfo = [
    { label: i18n.t('common.description'), value: description || itemDesc || '--' },
    {
      label: i18n.t('invoice.unit_price'),
      value: unit_price != null ? `$${formatAmount(unit_price)}` : '--',
    },
    {
      label: i18n.t('invoice.quantity'),
      value: q_humanized && quantity ? `${q_humanized} (${quantity})` : '--',
    },
    { label: i18n.t('invoice.unit'), value: unit || '--' },
    // TODO: Not provided by invoice item from BE
    //{ label: i18n.t("common.tax_percent"), value: TBD || "--" },
    //{ label: i18n.t("common.tax_value"), value: TBD || "--" },
  ];

  return (
    <CollapseCard
      title={
        <ListTitleComponent
          data-cy="invoice-item-element"
          title={name}
          quantity={quantity}
          unit={unit}
          total={total}
        />
      }
      customClasses={collapseCardClasses}
    >
      <List className={classes.list}>
        {invoiceItemInfo.map((info, index) => (
          <ListItem key={index} className={classes.listItem}>
            <ListItemText
              data-cy="item-data"
              primary={info.label}
              secondary={info.value}
              classes={listClasses}
            />
          </ListItem>
        ))}
      </List>
      {isEditable ? (
        <Grid container>
          <Link
            className={classes.invoiceActionButton}
            color="primary"
            href="#"
            onClick={handleEdit}
          >
            {i18n.t('common.edit')}
          </Link>
          <Divider className={classes.divider} orientation="vertical" flexItem />
          <Link
            className={classes.invoiceActionButton}
            color="primary"
            href="#"
            onClick={handleDelete}
          >
            {i18n.t('common.delete')}
          </Link>
        </Grid>
      ) : null}
    </CollapseCard>
  );
};

const InvoiceItems = ({ id, isStatementEntry }) => {
  const classes = useStyles();
  const listClasses = listTextStyles();
  const dispatch = useDispatch();

  const { invoice, loading, fetchingInvoiceItem } = useSelector((state) => state.invoice);
  const taxesEnabled = useSelector((state) => state.config?.settings?.core?.receivable?.taxes);

  const items = invoice?.invoice_items;
  const [openInvoiceModal, showInvoiceModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const [toggleDeleteItem, DeleteItemConfirm] = useConfirm({
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    title: i18n.t('invoice.delete_item'),
    message: i18n.t('invoice.delete_item_message'),
    onConfirm: useCallback(() => {
      dispatch(InvoiceActions.fetchDeleteItemFromInvoice(currentItem.id));
    }, [dispatch, currentItem]),
  });

  const handleClickModal = (item) => {
    setCurrentItem(item);
    showInvoiceModal((prevValue) => !prevValue);
  };
  const handleDeleteItem = (item) => {
    setCurrentItem(item);
    toggleDeleteItem();
  };

  const invoiceResume = [
    {
      label: i18n.t('invoice.subtotal'),
      value: `$${formatAmount((invoice?.invoiced || 0) - (invoice?.tax || 0))}`,
    },
    {
      label: i18n.t('invoice.total_tax'),
      value: `$${formatAmount(invoice?.tax)}`,
      hide: !taxesEnabled,
    },
    {
      label: i18n.t(isStatementEntry ? 'common.total_amount' : 'invoice.total_invoiced'),
      value: `$${formatAmount(invoice?.invoiced || 0)}`,
      className: classes.total,
    },
  ];

  const currentOrg = useSelector((state) => state.auth?.organizations?.current);
  const isEmitter = currentOrg?.id === invoice?.account_emitter?.holder_id;
  const allowNewItems = isEmitter;

  return (
    <Card
      title={
        <Grid container alignItems="center" justifyContent="space-between">
          <span>{i18n.t(isStatementEntry ? 'statement_entries.items' : 'invoice.items')}</span>
          {allowNewItems ? (
            <Button
              className={classes.addItemButton}
              color="primary"
              startIcon={<AddIcon />}
              style={{ textTransform: 'inherit' }}
              data-cy="add-item-btn"
              onClick={() => handleClickModal(null)}
            >
              {i18n.t('invoice.add_new_item')}
            </Button>
          ) : null}
        </Grid>
      }
      headerActionType="vertical"
      datacy="invoice-items-widget"
    >
      <DeleteItemConfirm />
      {openInvoiceModal && (
        <InvoiceItemModal
          invoice={id}
          open={openInvoiceModal}
          current={currentItem}
          loading={fetchingInvoiceItem}
          handleClick={handleClickModal}
        />
      )}
      <Box className={classes.root}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <Loading />
          </Box>
        ) : (
          <>
            {items?.length > 0 ? (
              <>
                <Box
                  style={{
                    height: '100%',
                    maxHeight: 'calc(100% - 52px)',
                    minHeight: 150,
                    overflowY: 'auto',
                  }}
                >
                  {items.map((item, index) => (
                    <ItemComponent
                      item={item}
                      key={index}
                      handleEdit={() => {
                        handleClickModal(item);
                      }}
                      handleDelete={() => {
                        handleDeleteItem(item);
                      }}
                    />
                  ))}
                  <List className={clsx(classes.list, classes.resume)}>
                    {invoiceResume.map(
                      (info, index) =>
                        !info.hide && (
                          <ListItem
                            data-cy="item-invoice-resume"
                            key={index}
                            className={classes.listItem}
                          >
                            <ListItemText
                              primary={info.label}
                              secondary={
                                <Typography
                                  data-cy="item-invoice-resume-value"
                                  variant="body2"
                                  component="span"
                                  color="textSecondary"
                                  className={info.className}
                                >
                                  {info.value}
                                </Typography>
                              }
                              classes={listClasses}
                            />
                          </ListItem>
                        )
                    )}
                  </List>
                </Box>
              </>
            ) : (
              <Typography variant="h4">
                <Box fontSize={14}>{i18n.t('common.empty_list')}</Box>
              </Typography>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

ListTitleComponent.propTypes = {
  title: PropTypes.string,
  total: PropTypes.any,
};

ItemComponent.propTypes = {
  item: PropTypes.object.isRequired,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
};

InvoiceItems.propTypes = {
  id: PropTypes.string.isRequired,
  isStatementEntry: PropTypes.bool,
};

export { InvoiceItems };
